"use client";

import { useEffect, useState } from "react";
import { getApiUrl } from "@/lib/api";

/**
 * Property stay quote from GET /api/public/stay-quote. The server prices the stay (each night at its
 * own seasonal or base rate, the same code POST /api/book charges with), so the page never works
 * out amounts itself and cannot drift from what the guest is charged.
 */
export type StayBreakdownLine = {
  nights: number;
  nightlyCents: number;
  rateName: string | null;
  totalCents: number;
};

export type StayQuote = {
  checkIn: string;
  checkOut: string;
  nights: number;
  breakdown: StayBreakdownLine[];
  baseNightlyCents: number;
  totalCents: number;
  depositCents: number | null;
  remainingCents: number | null;
  minNightsRequired: number;
  minNightsRateName: string | null;
  minStayError: string | null;
};

/** "£120" for whole pounds, "£120.50" otherwise. */
export function formatNightly(cents: number): string {
  return cents % 100 === 0 ? `£${cents / 100}` : `£${(cents / 100).toFixed(2)}`;
}

/** e.g. "3 × £120, 2 × £180 (Summer)" */
export function formatStayBreakdown(lines: StayBreakdownLine[]): string {
  return lines
    .map((l) => `${l.nights} × ${formatNightly(l.nightlyCents)}${l.rateName ? ` (${l.rateName})` : ""}`)
    .join(", ");
}

/** Dropdown label price: "from £X/night" when a season is cheaper than the base price. */
export function nightlyPriceLabel(priceCents: number, fromNightlyCents?: number | null): string {
  if (fromNightlyCents != null && fromNightlyCents < priceCents) return `from ${formatNightly(fromNightlyCents)}/night`;
  return `${formatNightly(priceCents)}/night`;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function useStayQuote(opts: {
  enabled: boolean;
  businessId: string;
  serviceId: string;
  checkIn: string;
  checkOut: string;
}): { quote: StayQuote | null; loading: boolean; error: string | null } {
  const { enabled, businessId, serviceId, checkIn, checkOut } = opts;
  const [state, setState] = useState<{ key: string; quote: StayQuote | null; loading: boolean; error: string | null }>({
    key: "",
    quote: null,
    loading: false,
    error: null,
  });
  const ready = enabled && !!businessId && !!serviceId && DATE_RE.test(checkIn) && DATE_RE.test(checkOut) && checkOut > checkIn;
  const key = ready ? `${businessId}|${serviceId}|${checkIn}|${checkOut}` : "";

  useEffect(() => {
    if (!key) {
      setState({ key: "", quote: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ key, quote: null, loading: true, error: null });
    const params = new URLSearchParams({ businessId, serviceId, checkIn, checkOut });
    fetch(`${getApiUrl()}/api/public/stay-quote?${params.toString()}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setState({ key, quote: null, loading: false, error: typeof data?.error === "string" ? data.error : "Could not price these dates." });
          return;
        }
        setState({ key, quote: data as StayQuote, loading: false, error: null });
      })
      .catch(() => {
        if (!cancelled) setState({ key, quote: null, loading: false, error: "Could not price these dates." });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (state.key !== key) return { quote: null, loading: !!key, error: null };
  return { quote: state.quote, loading: state.loading, error: state.error };
}
